import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import * as services from "../services/shiftsApi";
import { setIsLoading, setShiftsList } from "../slices/attendanceSlice";

function useShiftsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const shiftsList = useSelector((state) => state.attendance.shiftsList);
  const [pagination, setPagination] = useState({ page: 1, size: 10 });

  const getAttendanceShifts = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getAttendanceShiftsPaging({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(setShiftsList(response.data?.content || []));
        setPagination({
          page: params.page + 1,
          size: params.size,
          range: [
            params.page * params.size + 1,
            Math.min((params.page + 1) * params.size, response.data?.totalItems),
          ],
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch, setPagination]
  );

  const onChange = useCallback(
    ({ page, size }) => {
      getAttendanceShifts({ page: page - 1, size });
    },
    [getAttendanceShifts]
  );

  const onColor = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateAttendanceShiftsColor({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          actionId: currentEmployee,
        });
        getAttendanceShifts({
          page: 0,
          size: 10,
        });
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, currentCompany, currentEmployee, getAttendanceShifts]
  );

  const handleOnColor = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {
                t("dialog.confirmation.edit")
                  .replace("{entity}", t("shift.header"))}
            </p>
          </>
        ),
        onOk() {
          onColor(values);
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [onColor, t]
  );

  const onCreate = useCallback(() => {
    navigate("/configuration/attendance/shift/create");
  }, [navigate]);

  const onStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const statusFlag = val ? "ACTIVE" : "INACTIVE";
            const values = {
              attendanceSettingShiftId: record?.id,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag,
            };
            await services.manageStatusAttendanceShifts(values);
            message.success(t("common.success"))
            const modifiedShiftsList = shiftsList.map((s) => {
              if (s?.id === record?.id) {
                return { ...s, statusFlag };
              }
              return s;
            });
            dispatch(setShiftsList(modifiedShiftsList));
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })

    },
    [shiftsList, currentCompany, dispatch]
  );

  const onEdit = useCallback(
    (record) => {
      if (record?.id) {
        navigate(`/configuration/attendance/shift/${record?.id}/edit`);
      }
    },
    [navigate]
  );

  const onView = useCallback(
    (record) => {
      if (record?.id) {
        navigate(`/configuration/attendance/shift/${record?.id}/view`);
      }
    },
    [navigate]
  );

  const onSearch = useCallback(
    (values) => {
      values.page = 0;
      values.size = 50;
      getAttendanceShifts(values)
    },
    [navigate]
  );

  const onDelete = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          attendanceSettingShiftId: record?.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: "DELETE",
        };
        await services.manageStatusAttendanceShifts(values);
        message.success(t("common.success"))
        const modifiedShiftsList = shiftsList.map((s) => s?.id !== record?.id);
        dispatch(setShiftsList(modifiedShiftsList));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [shiftsList, currentCompany, dispatch]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getAttendanceShifts({
        page: 0,
        size: 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  return {
    isLoading,
    pagination,
    shiftsList,
    onCreate,
    onStatus,
    onEdit,
    onDelete,
    onChange,
    onSearch,
    onColor: handleOnColor,
    onView
  };
}

export default useShiftsList;
